import { useEffect, useRef } from "react"
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom"

import { Button } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { isNotFoundError } from "@/helpers/error.helper"
import { useCustomFunctionsForm } from "@/hooks/useCustomFunctionsForm"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { SaveOutlined } from "@/icons"
import { useWorkflow } from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import {
  getInitialValues,
  getWritePermission,
  transformActionsToPayload
} from "@/pages/GovernancePage/helpers/workflows"
import { AsideBreadcrumbPage } from "@/pages/SettingsPage/components/molecules/AsideBreadcrumbPage"
import { HeaderBreadcrumbPage } from "@/pages/SettingsPage/components/molecules/HeaderBreadcrumbPage"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import type { AutomatedActionPageComponent } from "."
import { AutomatedActionForm } from "../../organisms/AutomatedActionForm"

export const AutomatedActionPage: AutomatedActionPageComponent = (props) => {
  const { t } = useTranslation()
  const tPrefix = "settings.automatedActions.form."
  const breadcrumbKeyPrefix = `${tPrefix}sections.`
  const breadcrumbItems = [
    { key: "details", title: t(`${breadcrumbKeyPrefix}details`) },
    { key: "triggers", title: t(`${breadcrumbKeyPrefix}triggers`) },
    { key: "resources", title: t(`${breadcrumbKeyPrefix}resources`) },
    { key: "conditions", title: t(`${breadcrumbKeyPrefix}conditions`) },
    { key: "actions", title: t(`${breadcrumbKeyPrefix}actions`) }
  ]
  const { ROOT, WORKFLOWS } = NAMES_ROUTES.GOVERNANCE
  const formProps = useCustomFunctionsForm()
  const { isDirty } = formProps
  const { id = "new" } = useParams()
  const isNew = id === "new"
  const [{ response, isLoading, isError, error }, attr] = useWorkflow(
    { needle: id },
    { enabled: !!id && !isNew }
  )

  const initialValuesInData = response?.data[0]

  const { state } = useLocation()
  const { prePopulateData, duplicateData: initialValuesInState } = state || {}

  const initialValues =
    prePopulateData || initialValuesInState || initialValuesInData

  const navigate = useNavigate()
  const workflowsUrl = `/${ROOT}/${WORKFLOWS}`

  const isDuplicate = useRef(!!initialValuesInState).current
  const { logger, EVENTS } = useLogger()
  const { hasPermission } = useUser()

  const onFinish = async (
    values: ReturnType<typeof getInitialValues> & { "details.name": string }
  ) => {
    const prefix = "forms.messages"
    const isNew = id === "new"

    try {
      const name = values["details.name"].trim()
      const payload = {
        ...initialValues,
        name,
        description: values["details.description"]?.trim(),
        trigger: {
          category: values["trigger.category"],
          type: values["trigger.type"]
        },
        resourceConditions: values.conditions,
        filterConditions: values.currentConditions
          .filter((item: any) => !!item?.length)
          .map((item: any) => {
            return item.reduce((acc: any, { value, selectedValue }: any) => {
              acc[value] = selectedValue
              return acc
            }, {})
          }),
        actions: transformActionsToPayload(values.actions)
      }
      if (!payload.resourceConditions) payload.resourceConditions = []

      const { actions } = attr

      if (isNew) await actions.creator.mutateAsync({ workflow: payload })
      else
        await actions.updater.mutateAsync({
          needle: `/${id}`,
          data: { workflow: payload }
        })

      const action = isNew ? "added" : "updated"

      props.messageApi.success(t(`${prefix}.${action}`, { name }))

      formProps.setIsDirty(false)

      const { CREATE, EDIT } = EVENTS.ANALYTIC_EVENTS.WORKFLOW
      const event = isNew ? CREATE : EDIT
      const commonTags = { action: payload.actions[0].type, ...payload.trigger }
      const tags = isNew ? { isDuplicate, ...commonTags } : commonTags

      logger.info(event, tags)
    } catch (err: any) {
      const action = t(`general.${isNew ? "create" : "update"}`)
      const message = err?.message || t(`${prefix}.failed`, { action })

      props.messageApi.error(message)

      const custom_error = "ERROR_CREATE_OR_UPDATE_WORKFLOW"
      const tags = { action, message, custom_error }

      logger.error(err, { tags })
    }
  }

  useEffect(() => {
    if (prePopulateData || initialValuesInState) {
      formProps.setIsDirty(true)
      navigate("", { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isError && isNotFoundError(error)) {
    return <Navigate to={`/${ROOT}/${WORKFLOWS}`} />
  }

  return (
    <PageWithBreadcrumbTemplate
      loading={isLoading}
      header={
        <HeaderBreadcrumbPage
          isLoading={false}
          items={[
            {
              title: t("navigation.governance"),
              to: `/${ROOT}`
            },
            {
              title: t("navigation.automatedActions"),
              to: workflowsUrl
            },
            {
              title: isNew
                ? t("settings.automatedActions.table.actions.create")
                : `${initialValues?.name || "..."}`
            }
          ]}
        />
      }
      aside={<AsideBreadcrumbPage items={breadcrumbItems} />}
      footer={
        <div className="container-buttons-form-footer-page-with-breadcrumb-template-settings">
          <Button onClick={() => navigate(workflowsUrl)}>
            {t("general.cancel")}
          </Button>
          <Button
            form="automation-action-form"
            htmlType="submit"
            type="primary"
            icon={<SaveOutlined />}
            loading={attr.saving}
            disabled={!isDirty || !hasPermission(getWritePermission())}
          >
            {t(isNew ? `${tPrefix}buttons.submit` : "general.saveUpdate")}
          </Button>
        </div>
      }
    >
      <AutomatedActionForm
        initialValues={getInitialValues(initialValues)}
        onFinish={onFinish}
        {...formProps}
        {...attr}
      />
    </PageWithBreadcrumbTemplate>
  )
}
